import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Observable, finalize, shareReplay } from 'rxjs';

/**
 * Prevents duplicate identical requests from being sent simultaneously.
 * Only blocks concurrent requests with same method, URL, and body.
 * Cleans up automatically when request completes.
 */

// Track in-flight requests
const pendingRequests = new Map<string, Observable<HttpEvent<any>>>();
const cleanupTimeouts = new Map<string, any>();

function getRequestKey(req: HttpRequest<any>): string {
  const method = req.method;
  const url = req.urlWithParams;
  
  // Skip body serialization for FormData (file uploads, etc.)
  let body = '';
  if (req.body) {
    if (req.body instanceof FormData) {
      body = '[FormData]'; // Don't prevent duplicates for file uploads
    } else {
      try {
        body = JSON.stringify(req.body);
      } catch {
        body = String(req.body);
      }
    }
  }
  
  return `${method}:${url}:${body}`;
}

function shouldPreventDuplicates(method: string): boolean {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
}

export const requestDuplicatePreventionInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>, 
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Skip GET, HEAD, OPTIONS - only protect mutations
  if (!shouldPreventDuplicates(req.method)) {
    return next(req);
  }

  const requestKey = getRequestKey(req);

  // Return existing request if already in-flight
  if (pendingRequests.has(requestKey)) {
    console.warn('[DuplicatePrevention] Blocked:', req.method, req.url);
    return pendingRequests.get(requestKey)!;
  }

  // Share request and cleanup when done
  const sharedRequest = next(req).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
    finalize(() => {
      // Clear any existing timeout
      if (cleanupTimeouts.has(requestKey)) {
        clearTimeout(cleanupTimeouts.get(requestKey));
        cleanupTimeouts.delete(requestKey);
      }
      
      pendingRequests.delete(requestKey);
      
      // Backup cleanup after 1 second
      const timeoutId = setTimeout(() => {
        pendingRequests.delete(requestKey);
        cleanupTimeouts.delete(requestKey);
      }, 1000);
      
      cleanupTimeouts.set(requestKey, timeoutId);
    })
  );

  pendingRequests.set(requestKey, sharedRequest);
  return sharedRequest;
};
